import { useEffect, useRef, useState, type FormEvent } from "react";
import { Building2, CreditCard, Lock, ShoppingBag } from "lucide-react";
import { ApiError, type CheckoutInput, type DeliveryOption } from "../../../lib/api";
import { formatMoney } from "../../../lib/money";
import { placeOrder } from "../../../lib/store-api";
import { cartSubtotal, lineKey, useCart, useShop } from "../../../stores/cart";
import { useStorefrontContext } from "./StorefrontContext";

function newKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function deliveryFee(option: DeliveryOption | undefined, subtotal: number): number {
  if (!option) return 0;
  if (option.freeOverMinor && subtotal >= option.freeOverMinor) return 0;
  return option.feeMinor;
}

const PAYMENT_COPY: Record<string, { title: string; hint: string; Icon: typeof CreditCard }> = {
  online: { title: "Pay online", hint: "Card, bank or USSD — secure checkout", Icon: CreditCard },
  bank_transfer: { title: "Bank transfer", hint: "We'll show the account details after you order", Icon: Building2 },
};

export function CheckoutPage() {
  const { store, slug, paths, go } = useStorefrontContext();
  const lines = useCart(slug);
  const clear = useShop((s) => s.clear);
  // One key per checkout attempt: a double-click or retry returns the same order.
  const idempotencyKey = useRef(newKey());

  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [address, setAddress] = useState({ line1: "", city: "", state: "", notes: "" });
  const [deliveryId, setDeliveryId] = useState(store.deliveryOptions[0]?.id ?? "");
  const [payment, setPayment] = useState(store.paymentMethods[0] ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `Checkout · ${store.name}`;
  }, [store.name]);

  if (lines.length === 0) {
    return (
      <section className="zs-page zs-empty">
        <ShoppingBag size={36} aria-hidden="true" />
        <h1 className="zs-title">Your cart is empty</h1>
        <button type="button" className="zs-btn" onClick={() => go(paths.shop)}>
          Browse the shop
        </button>
      </section>
    );
  }

  const delivery = store.deliveryOptions.find((d) => d.id === deliveryId);
  const subtotal = cartSubtotal(lines);
  const fee = deliveryFee(delivery, subtotal);
  const acceptingOrders = store.paymentMethods.length > 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const input: CheckoutInput = {
      items: lines.map((l) => ({ productId: l.productId, quantity: l.quantity, options: l.options })),
      customer,
      address: delivery?.pickup ? null : address,
      deliveryOptionId: deliveryId,
      paymentMethod: payment as CheckoutInput["paymentMethod"],
      idempotencyKey: idempotencyKey.current,
    };
    try {
      const result = await placeOrder(slug, input);
      clear(slug);
      const target = result.paymentUrl ?? paths.order(result.order.id, result.accessToken);
      if (target.startsWith(window.location.origin)) {
        go(target.slice(window.location.origin.length));
      } else if (target.startsWith("/")) {
        go(target);
      } else {
        window.location.assign(target);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "We couldn't place your order. Please try again.");
      setSubmitting(false);
    }
  };

  const field = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    props: Record<string, unknown> = {},
  ) => (
    <label className="zs-field">
      <span>{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} {...props} />
    </label>
  );

  return (
    <section className="zs-page">
      <h1 className="zs-title">Checkout</h1>
      {!acceptingOrders ? (
        <p className="zs-notice" role="status">
          This store isn't taking orders yet. Please check back soon.
        </p>
      ) : null}
      <form className="zs-checkout" onSubmit={submit}>
        <div className="zs-checkout__main">
          <fieldset className="zs-panel">
            <legend>Contact</legend>
            {field("Full name", customer.name, (v) => setCustomer({ ...customer, name: v }), {
              required: true,
              autoComplete: "name",
            })}
            <div className="zs-field-row">
              {field("Email", customer.email, (v) => setCustomer({ ...customer, email: v }), {
                type: "email",
                required: true,
                autoComplete: "email",
              })}
              {field("Phone", customer.phone, (v) => setCustomer({ ...customer, phone: v }), {
                type: "tel",
                required: true,
                autoComplete: "tel",
              })}
            </div>
          </fieldset>

          <fieldset className="zs-panel">
            <legend>Delivery</legend>
            <div className="zs-choices">
              {store.deliveryOptions.map((option) => (
                <label key={option.id} className="zs-choice">
                  <input
                    type="radio"
                    name="delivery"
                    value={option.id}
                    checked={deliveryId === option.id}
                    onChange={() => setDeliveryId(option.id)}
                  />
                  <span className="zs-choice__body">
                    <strong>{option.name}</strong>
                    {option.description ? <span className="zs-muted">{option.description}</span> : null}
                  </span>
                  <span className="zs-choice__price">
                    {deliveryFee(option, subtotal) === 0 ? "Free" : formatMoney(option.feeMinor, store.currency)}
                  </span>
                </label>
              ))}
            </div>
            {delivery && !delivery.pickup ? (
              <div className="zs-address">
                {field("Address", address.line1, (v) => setAddress({ ...address, line1: v }), {
                  required: true,
                  autoComplete: "street-address",
                })}
                <div className="zs-field-row">
                  {field("City", address.city, (v) => setAddress({ ...address, city: v }), {
                    required: true,
                    autoComplete: "address-level2",
                  })}
                  {field("State", address.state, (v) => setAddress({ ...address, state: v }), {
                    autoComplete: "address-level1",
                  })}
                </div>
                {field("Delivery notes (optional)", address.notes, (v) => setAddress({ ...address, notes: v }), {
                  maxLength: 300,
                })}
              </div>
            ) : null}
          </fieldset>

          {acceptingOrders ? (
            <fieldset className="zs-panel">
              <legend>Payment</legend>
              <div className="zs-choices">
                {store.paymentMethods.map((method) => {
                  const copy = PAYMENT_COPY[method] ?? PAYMENT_COPY.online!;
                  const Icon = copy.Icon;
                  return (
                    <label key={method} className="zs-choice">
                      <input
                        type="radio"
                        name="payment"
                        value={method}
                        checked={payment === method}
                        onChange={() => setPayment(method)}
                      />
                      <Icon size={20} aria-hidden="true" className="zs-choice__icon" />
                      <span className="zs-choice__body">
                        <strong>{copy.title}</strong>
                        <span className="zs-muted">{copy.hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ) : null}
        </div>

        <aside className="zs-panel zs-summary" aria-label="Order summary">
          <h2 className="zs-subtitle">Order summary</h2>
          <ul className="zs-summary__lines">
            {lines.map((line) => (
              <li key={lineKey(line)}>
                <div className="zs-summary__media">
                  {line.image ? <img src={line.image} alt="" /> : null}
                  <span className="zs-summary__qty">{line.quantity}</span>
                </div>
                <div className="zs-summary__name">
                  <span>{line.name}</span>
                  {Object.keys(line.options).length > 0 ? (
                    <span className="zs-muted">{Object.values(line.options).join(" · ")}</span>
                  ) : null}
                </div>
                <span>{formatMoney(line.priceMinor * line.quantity, store.currency)}</span>
              </li>
            ))}
          </ul>
          <div className="zs-sum-row">
            <span>Subtotal</span>
            <span>{formatMoney(subtotal, store.currency)}</span>
          </div>
          <div className="zs-sum-row">
            <span>{delivery?.name ?? "Delivery"}</span>
            <span>{fee === 0 ? "Free" : formatMoney(fee, store.currency)}</span>
          </div>
          <div className="zs-sum-row zs-sum-row--total">
            <span>Total</span>
            <span>{formatMoney(subtotal + fee, store.currency)}</span>
          </div>
          {error ? (
            <p className="zs-error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            className="zs-btn zs-btn--block"
            disabled={!acceptingOrders || submitting || !deliveryId || !payment}
          >
            {submitting ? "Placing order…" : payment === "online" ? "Continue to payment" : "Place order"}
          </button>
          <p className="zs-muted zs-secure">
            <Lock size={12} aria-hidden="true" /> Prices are confirmed when you place your order.
          </p>
        </aside>
      </form>
    </section>
  );
}
